import {
  expr as exprSource,
  templateDescription,
  type ExerciseNode,
  type GroupKind,
  type Line,
} from '@gymcloud/mixedscript'

import { parseTemplate, type Enrollment, type WorkoutsDone } from '~/data/templates'
import { formatDayRangeShort, formatDayShort } from '~/lib/dates'
import { describeLine, primaryText, weightText } from '~/lib/line-shape'
import { cycleOf, dayOf, inDayOrder, scheduleOf } from '~/lib/template-tree'
import { weekdayName } from '~/lib/weekdays'

/**
 * What the one page every template has is made of — `@specs/features/explore.spec.md`, A
 * template's page. The reading is here rather than in the route because the page is assembled
 * from a template in your library or from a listing in Explore, which are two ways of holding
 * one thing, and neither should have to know how the other is read.
 */

/** What a template is, however it was reached: your own copy of it, or its listing. */
export interface TemplateFacts {
  readonly templateId: string
  readonly name: string
  readonly script: string
  readonly description: string | null
  /** Null where it is your own and unpublished — there is nobody to name. */
  readonly authorName: string | null
  readonly mine: boolean
  /** Whether it stands in Explore, and what people have done with it there. */
  readonly published: boolean
  readonly ranCount: number | null
  readonly runningCount: number | null
}

/** One exercise a workout holds, and what each of its lines asks for. */
export interface HeldExercise {
  readonly name: string
  readonly lines: string[]
}

/** One thing a workout holds: an exercise on its own, or a group of them. */
export type Holding =
  | { readonly kind: 'exercise'; readonly exercise: HeldExercise }
  | { readonly kind: 'group'; readonly group: GroupKind; readonly exercises: HeldExercise[] }

/** One workout the template holds, in the order it is performed. */
export interface TemplateWorkout {
  readonly name: string
  /** The weekday it sits on under a strict schedule, and null under any other. */
  readonly day: string | null
  readonly holdings: Holding[]
}

/**
 * What one line of an exercise asks for, read as words rather than as the script it is written
 * in. A page about a program is read by somebody deciding whether to follow it, not by somebody
 * editing it — so it reads the way the editor's own rows read, which is what makes a template
 * look like itself wherever it is opened.
 */
export function holdingLine(line: Line): string {
  if (line.type !== 'set') {
    return describeLine(line)
  }
  const parts = [
    ...(line.count === null ? [] : [exprSource(line.count), '×']),
    primaryText(line.primary),
    ...(line.weight === null ? [] : ['@', weightText(line.weight)]),
  ]
  const asked = parts.join(' ')
  return line.warmup ? `${asked} · warm-up` : asked
}

function heldExercise(exercise: ExerciseNode): HeldExercise {
  return {
    name: exercise.name === '' ? 'Unnamed exercise' : exercise.name,
    lines: exercise.lines.map(holdingLine),
  }
}

/**
 * The workouts a script holds, each read as what it actually asks of you: its exercises in the
 * order they are performed, and a superset, a choose or a rotate as the group it is. A list of
 * bare names says which exercises are in a program; it does not say what the program is.
 */
export function workoutsOf(script: string): TemplateWorkout[] {
  try {
    const node = parseTemplate(script)
    const strict = scheduleOf(node) === 'strict'
    const weeks = Math.ceil((cycleOf(node) ?? 7) / 7)
    // Under a strict schedule the workouts read in the order their days fall, whatever order
    // the script lists them in.
    return inDayOrder(node).workouts.map((workout) => {
      const day = strict ? dayOf(workout) : null
      return {
        name: workout.name === '' ? 'Unnamed workout' : workout.name,
        day:
          day === null
            ? null
            : weeks > 1
              ? `${weekdayName(day)}, week ${String(Math.floor(day / 7) + 1)}`
              : weekdayName(day),
        holdings: workout.items.map((item) =>
          item.type === 'exercise'
            ? { kind: 'exercise' as const, exercise: heldExercise(item.exercise) }
            : {
                kind: 'group' as const,
                group: item.kind,
                exercises: item.exercises.map(heldExercise),
              },
        ),
      }
    })
  } catch {
    return []
  }
}

/** What a template calls its shape: the schedule it runs, and the length of its cycle. */
export function shapeOf(script: string): string {
  try {
    const node = parseTemplate(script)
    const schedule = scheduleOf(node)
    const named = schedule.charAt(0).toUpperCase() + schedule.slice(1)
    const cycle = cycleOf(node)
    return schedule === 'strict' && cycle !== null ? `${named}, ${String(cycle)} days` : named
  } catch {
    return 'The script does not parse'
  }
}

/** The description a script carries, or null where it carries none worth showing. */
export function descriptionOf(script: string): string | null {
  try {
    const said = templateDescription(parseTemplate(script))
    return said !== null && said.trim() !== '' ? said : null
  } catch {
    return null
  }
}

/** Your runs of one template, newest first, each with what it came to. */
export function runsOf(enrollments: readonly Enrollment[], templateId: string): Enrollment[] {
  return enrollments
    .filter((enrollment) => enrollment.templateId === templateId)
    .toSorted((a, b) => b.startedAt - a.startedAt)
}

/** How many workouts you have finished across every run of it. */
export function workoutsDone(runs: readonly Enrollment[]): number {
  return runs.reduce((total, run) => total + (run.workoutCount ?? 0), 0)
}

/** How many workouts a run came to, in words. */
export function runTally(count: number): string {
  return `${String(count)} ${count === 1 ? 'workout' : 'workouts'}`
}

/** The days a run spanned, with the one still going saying so rather than naming today. */
export function runSpan(run: Enrollment): string {
  return run.endedAt === null
    ? `Since ${formatDayShort(run.startedAt)}`
    : formatDayRangeShort(run.startedAt, run.endedAt)
}

/**
 * What a run says under its dates: the one still going says it is, and one behind you says how
 * long it lasted. A length is only ever said of a run with both ends, so nothing here has to
 * read a clock — a page that counted the days of the run underway would say something different
 * every time it was opened.
 */
export function runDetail(run: Enrollment): string {
  if (run.endedAt === null) {
    return 'Running now'
  }
  const days = Math.floor((run.endedAt - run.startedAt) / 86_400_000)
  if (days < 14) {
    const held = Math.max(days, 1)
    return `${String(held)} ${held === 1 ? 'day' : 'days'}`
  }
  return `${String(Math.floor(days / 7))} weeks`
}

/** What you have done of each workout, by name, for a page to read off. */
export function doneByWorkout(done: readonly WorkoutsDone[]): Map<string, number> {
  return new Map(done.map((entry) => [entry.name, entry.count]))
}
